// src/constants/seo-builders.ts
// ============================================================================
// SSOT — All SEO builders, helpers & types in one place (React + build scripts)
// ============================================================================
import {
  POSTER_URL,
  OG_PRIMARY_IMAGE,
  INCLUDE_SSR_VIDEO,
  seoBuildSlug,
  type SeoService,
  IS_PROD,
  SITE_BASE,
  PRIMARY_KEYWORD_BY_SERVICE,
  businessOfficeMapUrl,
  type BusinessOffice,
} from './core';

import {
  WORDS,
  HOME_DESC,HOME_HERO_TITLE,           
  BRAND_TITLE_HE,
  BRAND_PREFIX_RE,
  BRAND_SUFFIX_RE,
  BUSINESS,
  HTML_LANG_FULL,
} from '../i18n/locales/i18n.he';

import type { EventType, SeoCity } from './geo';

import type { InternalLink } from '@/utils/internalLinks';
import { buildJsonLdScriptTag } from './jsonld-utils';

import {
  buildSeoEventBundle,
  mapInternalLinks as mapInternalLinksEnhanced,
} from './seo-event-enhanced';
import {
  getBackgroundImagesForEvent,
  getBackgroundImageSrcSetForEvent,
} from './images';

export type BuildVideoLdParams = {
  siteBase: string;           
  title: string;
  videoUrl: string;           
  uploadDate: string;         
  durationISO8601: string;    
  pageUrl?: string;           
};
export const siteBaseNoSlash = (raw: string) => String(raw).replace(/\/$/, '');

export const escapeHtml = (s: string) =>
  String(s)
    .replace(/&/g,'&amp;')
    .replace(/</g,'&lt;')
    .replace(/>/g,'&gt;')
    .replace(/"/g,'&quot;');

function ensureTrailingSlashAbs(url: string) {
  try {
    const u = new URL(url);
    if (!u.pathname.endsWith('/')) u.pathname += '/';
    return u.toString();
  } catch {
    // fallback לנתיבים לא-אבסולוטיים
    return url.endsWith('/') ? url : `${url}/`;
  }
}

export function absUrl(pathOrAbs: string) {
  try {
    // אם כבר אבסולוטי
    const already = new URL(pathOrAbs);
    return already.toString();
  } catch {}

  const p = pathOrAbs.startsWith('/') ? pathOrAbs : `/${pathOrAbs}`;
  const base = new URL(SITE_BASE);
  // decode ואז encode של כל segment בנפרד (מונע כפילויות/קצוות)
  const decoded = (() => { try { return decodeURI(p); } catch { return p; } })();
  base.pathname = decoded
    .split('/')
    .map((seg, i) => (i === 0 ? '' : encodeURIComponent(seg)))
    .join('/');

  return base.toString();
}

export function buildCanonicalUrl(slugOrPath: string) {
  const raw = String(slugOrPath || '').trim();

  try {
    const u = new URL(raw);
    if (!u.pathname.endsWith('/')) u.pathname += '/';
    return u.toString();
  } catch {
  }

  const cleaned = raw.replace(/^\/+|\/+$/g, '');
  return ensureTrailingSlashAbs(absUrl(`/${cleaned}`));
}

function stripTrailingBrandPart(t: string) {
  return String(t).replace(/\s*\|\s*.+$/, '').trim();
}

export function brandFirstTitle(raw: string) {
  const base = stripTrailingBrandPart(raw);
  if (base.startsWith(BRAND_TITLE_HE)) return base;
  return `${BRAND_TITLE_HE} | ${base}`;
}

export const stripBrandAnywhere = (t: string) =>
  t.replace(BRAND_PREFIX_RE, '').replace(BRAND_SUFFIX_RE, '').trim();

export const stripTrailingBrand = (t: string) =>
  t.replace(BRAND_SUFFIX_RE, '').trim();

export function isVenueName(_x: unknown): false {
  return false;
}

export function canonicalSlugFor(service: SeoService, city: SeoCity) {
  const primary = PRIMARY_KEYWORD_BY_SERVICE[service];
  return seoBuildSlug(primary, city);
}

export const SECTION_ONE_HEAD_DEFAULTS = {
  preloadImageHref: POSTER_URL,
  // אין VideoObject בעמוד הבית כברירת מחדל
  videoLd: (_override?: Partial<Parameters<typeof buildVideoLd>[0]>) => null,
  og: () =>
    buildOgMeta({
      // שימוש בקבועים של עמוד הבית
      title: HOME_HERO_TITLE,
      description: HOME_DESC,
      image: OG_PRIMARY_IMAGE
      // לא מעבירים videoUrl -> og:type = "website"
    }),
} as const;

export function buildOgMeta(params: {
  title: string;
  description: string;
  image?: string;
  videoUrl?: string;
  videoWidth?: number | string;
  videoHeight?: number | string;
}): ReadonlyArray<{ property: string; content: string }> {
  const {
    title,
    description,
    image = OG_PRIMARY_IMAGE,
    videoUrl,
    videoWidth = 1280,
    videoHeight = 720,
  } = params;

  const imageAbs = image.startsWith('http') ? image : absUrl(image);
  const videoAbs = videoUrl ? (videoUrl.startsWith('http') ? videoUrl : absUrl(videoUrl)) : undefined;

  return [
    { property: 'og:type', content: videoAbs ? 'video.other' : 'website' },
    ...(videoAbs
      ? [
          { property: 'og:video', content: String(videoAbs) },
          { property: 'og:video:secure_url', content: String(videoAbs) },
          { property: 'og:video:type', content: 'video/mp4' },
          { property: 'og:video:width', content: String(videoWidth) },
          { property: 'og:video:height', content: String(videoHeight) },
        ]
      : []),
    { property: 'og:image', content: String(imageAbs) },
    { property: 'og:image:secure_url', content: String(imageAbs) },
    { property: 'og:title', content: String(title) },
    { property: 'og:description', content: String(description) },
  ] as const;
}

export function buildVideoLd(p: BuildVideoLdParams) {
  const obj: any = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: p.title,
    description: `${BRAND_TITLE_HE} – ${WORDS.mc} ${WORDS.events.generic.to}, ${WORDS.events.wedding.one}, ${WORDS.events.henna.one} או ${WORDS.events.bar_bat.one} עם אווירה מחשמלת.`,    // רק ה-thumbnail של הווידאו עובר לפוסטר 16:9:
    thumbnailUrl: [POSTER_URL],
    uploadDate: p.uploadDate,
    duration: p.durationISO8601,
    contentUrl: p.videoUrl,
    inLanguage: HTML_LANG_FULL,
    publisher: {
      '@type': 'Organization',
      name: BRAND_TITLE_HE,
      logo: { '@id': `${p.siteBase}#logo` },
    },
    potentialAction: [
      { '@type': 'WatchAction', target: p.videoUrl },
    ],
    isFamilyFriendly: true,
  };

  return obj;
}

export function buildFaqLd(items: Array<{ q: string; a: string }>) {
  const cleaned = (Array.isArray(items) ? items : [])
    .map((x) => ({
      q: typeof x?.q === 'string' ? x.q.trim() : '',
      a: typeof x?.a === 'string' ? x.a.trim() : '',
    }))
    .filter((x) => x.q.length >= 3 && x.a.length >= 3);

  if (!cleaned.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: cleaned.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };
}

export function buildItemListLd(items: Array<{ href: string; label: string }> | InternalLink[]) {
  if (!items?.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((l, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: l.label,
      item: {
        '@type': 'WebPage',
        name: l.label,
        url: buildCanonicalUrl(l.href),
      },
    })),
  };
}

export function buildWebPageLd(opts: {
  name: string;
  url: string;
  description: string;
  primaryImage?: string;
  inLanguage?: string;
  datePublished?: string;
  dateModified?: string;
  breadcrumbId?: string;
  mainEntityId?: string;
}) {
  const {
    name, url, description,
    primaryImage = OG_PRIMARY_IMAGE,
    inLanguage = HTML_LANG_FULL,
    datePublished, dateModified,
    breadcrumbId,
    mainEntityId,
  } = opts;

  const obj: any = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    url,
    inLanguage,
    isPartOf: { '@id': `${SITE_BASE}#website` },
    description,
  };

  if (primaryImage) {
    const imgAbs = primaryImage.startsWith('http') ? primaryImage : absUrl(primaryImage);
    obj.primaryImageOfPage = { '@type': 'ImageObject', url: imgAbs };
  }
    if (datePublished) obj.datePublished = datePublished;
  if (dateModified)  obj.dateModified  = dateModified;
  if (breadcrumbId)  obj.breadcrumb    = { '@id': breadcrumbId };
  if (mainEntityId)  obj.mainEntity    = { '@id': mainEntityId };

  // 👇 חדש — חיבור מפורש לישות העסק
  (obj as any).about = { '@id': `${SITE_BASE}#business` };

  return obj;
}

export function buildBusinessLd(): any;
export function buildBusinessLd(_isVenue: boolean, _cityOrVenue?: string): any;
export function buildBusinessLd(opts?: {
  includeAreaServed?: boolean;   
  areaName?: string;             
  includeOpeningHours?: boolean;
  office?: BusinessOffice;
}): any;

export function buildBusinessLd(
  optsOrIsVenue?: { includeAreaServed?: boolean; areaName?: string; includeOpeningHours?: boolean; office?: BusinessOffice } | boolean,
  maybeCity?: string
) {
  // תמיכה בחתימה ההיסטורית: buildBusinessLd(true, 'באר שבע')
  let mapped: {
    includeAreaServed?: boolean;
    areaName?: string;
    includeOpeningHours?: boolean;
    office?: BusinessOffice;
  };
  if (typeof optsOrIsVenue === 'boolean') {
    mapped = {
      includeAreaServed: !!maybeCity, // מוסיף גם City אם סופקה עיר
      areaName: maybeCity,
      includeOpeningHours: true,
    };
  } else {
    mapped = optsOrIsVenue ?? {};
  }

  const {
    includeAreaServed = false,
    areaName,
    includeOpeningHours = true,
    office,
  } = mapped;
  const address = {
    street: office?.address.street ?? BUSINESS.address.street,
    city: office?.address.city ?? BUSINESS.address.city,
    postal: office ? office.address.postal : BUSINESS.address.postal,
    country: office?.address.country ?? BUSINESS.address.country,
    lat: office ? office.address.lat : BUSINESS.address.lat,
    lng: office ? office.address.lng : BUSINESS.address.lng,
    mapUrl: office ? businessOfficeMapUrl(office) : BUSINESS.address?.mapUrl,
  };

  const rawPlaceId =
    office && Object.prototype.hasOwnProperty.call(office, 'googlePlaceId')
      ? office.googlePlaceId
      : (BUSINESS as any).googlePlaceId;

  const googlePlaceId =
    typeof rawPlaceId === 'string' && rawPlaceId.trim()
      ? rawPlaceId.trim()
      : undefined;

  // URL קנוני של Google Place ID (אם זמין), כדי לאחד זהויות ולמנוע כפילויות
  const placeUrlCanonical =
    googlePlaceId
      ? `https://www.google.com/maps/place/?q=place_id:${googlePlaceId}`
      : undefined;

  // גיבוי מפה מתוך הכתובת אם אין placeId
  const mapsFromAddress = (() => {
    try {
      const q = encodeURIComponent(
        [address.street, address.city].filter(Boolean).join(', ')
      );
      return `https://www.google.com/maps?q=${q}`;
    } catch {
      return undefined;
    }
  })();

  const fallbackMapUrl = address.mapUrl || mapsFromAddress;
  const hasMapUrl = placeUrlCanonical || fallbackMapUrl;
  const sameAsBase = office?.sameAs ? Array.from(office.sameAs) : [...(BUSINESS.sameAs || [])];
  const sameAsMapUrls = office
    ? placeUrlCanonical
      ? [placeUrlCanonical]
      : []
    : [placeUrlCanonical, fallbackMapUrl].filter(Boolean);

  // --- הישות העסקית: LocalBusiness עם subtype ספציפי שנתמך דרך @type ---
  const out: any = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'EntertainmentBusiness'],

    '@id': `${SITE_BASE}#business`,
    name: BUSINESS.name,
    url: SITE_BASE,
    telephone: BUSINESS.phone,
    email: BUSINESS.email,
    logo: { '@id': `${SITE_BASE}#logo` },
    image: OG_PRIMARY_IMAGE,

    address: {
      '@type': 'PostalAddress',
      streetAddress: address.street,
      addressLocality: address.city,
      ...(address.postal ? { postalCode: address.postal } : {}),
      addressCountry: address.country,
    },

    priceRange: '₪₪',

    sameAs: Array.from(
      new Set<string>([
        ...sameAsBase,
        ...(sameAsMapUrls as string[]),
      ])
    ),
  };

  if (hasMapUrl) out.hasMap = hasMapUrl;

  if (
    typeof address.lat === 'number' &&
    typeof address.lng === 'number'
  ) {
    out.geo = {
      '@type': 'GeoCoordinates',
      latitude: address.lat,
      longitude: address.lng,
    };
  }

  // מזהה קנוני ל-Google Place ID (לטובת איחוד ישויות בגרף)
  if (googlePlaceId) {
    out.identifier = {
      '@type': 'PropertyValue',
      propertyID: 'google:place_id',
      value: googlePlaceId,
    };
  }

  // --- אזור שירות: ברירת מחדל כל ישראל (Country: IL),
  // ואם ביקשו includeAreaServed + areaName → נוסיף גם City ספציפית בנוסף ל-IL ---
  const areaServed: any[] = [{ '@type': 'Country', name: 'Israel' }];
  if (includeAreaServed && areaName) {
      const n = String(areaName).trim();
      if (n && n !== 'ישראל') areaServed.push({ '@type': 'City', name: n });
      }
  out.areaServed = areaServed;

  // --- שעות פעילות ברירת מחדל (ניתן לביטול דרך includeOpeningHours=false) ---
  if (includeOpeningHours) {
    out.openingHoursSpecification = [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Sunday'],
        opens: '09:00',
        closes: '21:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Friday'],
        opens: '09:00',
        closes: '15:00',
      },
    ];
  }

  // ContactPoint בסיסי לשירות לקוחות בעברית
  out.contactPoint = [
    {
      '@type': 'ContactPoint',
      telephone: BUSINESS.phone,
      contactType: 'customer service',
      areaServed: 'Israel',
      availableLanguage: ['he'],
    },
  ];

  return out;
}
export function buildWebsiteLd() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_BASE}#website`,
    url: SITE_BASE,
    name: BUSINESS.name,
  };
}

export function buildServiceLd(
  service: string,
  cityOrVenue: string,
  _isVenue: boolean,
  opts?: {
    inLanguage?: string;
    alternateNames?: string[];
    mainEntityOfPageId?: string;
  }
) {
  const inLanguage = opts?.inLanguage ?? HTML_LANG_FULL;
  const city = String(cityOrVenue ?? '').trim();
  const isCountry = !city || city === 'ישראל';

  const node: any = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: isCountry ? service : `${service} ב${city}`,
    serviceType: service,
    inLanguage,
    image: `${SITE_BASE}/logo512.png`,
    areaServed: isCountry
      ? { '@type': 'Country', name: 'Israel' }
      : { '@type': 'City', name: city },
    provider: { '@id': `${SITE_BASE}#business` },
  };

  if (opts?.mainEntityOfPageId) node.mainEntityOfPage = { '@id': opts.mainEntityOfPageId };
  return node;
}

export function buildBreadcrumbLd(pageTitle: string, canonicalUrl: string) {
  let cleanName = pageTitle.includes('|')
    ? pageTitle.split('|').pop()!.trim()
    : pageTitle.trim();

    cleanName = cleanName
    .replace(BRAND_PREFIX_RE, '')
    .replace(BRAND_SUFFIX_RE, '')
    .trim();

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'בית', item: SITE_BASE },
      { '@type': 'ListItem', position: 2, name: cleanName, item: canonicalUrl },
    ],
  };
}

export function buildLogoLd() {
  return {
    '@type': 'ImageObject',
    '@id': `${SITE_BASE}#logo`,
    url: `${SITE_BASE}/logo512.png`,
    width: 512,
    height: 512,
  };
}

const getInitialHeroImageAssets = (
  eventType: EventType = 'אירוע',
  image?: string,
) => {
  const eventBackgrounds = getBackgroundImagesForEvent(eventType);

  return {
    mobile: image || eventBackgrounds.dark.mobile[0],
    desktop: image || eventBackgrounds.dark.desktop[0],
    mobileSrcSet: image
      ? undefined
      : getBackgroundImageSrcSetForEvent(eventType, 'dark', 'mobile', 0),
    desktopSrcSet: image
      ? undefined
      : getBackgroundImageSrcSetForEvent(eventType, 'dark', 'desktop', 0),
  };
};

export function performanceHints(opts: {
  ogImage?: string;
  preloadOgImage?: boolean;
  loaderImage?: string;
  videoUrl?: string;
  preloadVideo?: boolean;
  mode?: 'prerender' | 'spa';
  preconnectOrigins?: string[];
  eventType?: EventType;
}) {
  const {
    videoUrl,
    preloadVideo = false,
    mode = 'prerender',
    preconnectOrigins = [],
    eventType = 'אירוע',
    loaderImage,
  } = opts;

  // 1) מאחדים origins בלי כפילויות
  const originSet = new Set<string>();

  for (const o of preconnectOrigins) {
    if (o) originSet.add(String(o).trim());
  }

  if (loaderImage) {
    try {
      originSet.add(new URL(loaderImage).origin);
    } catch {
      // ignore
    }
  }

  // 2) מדפיסים פעם אחת לכל origin
  const toOrigin = (raw: string) => {
    const s = String(raw).trim().replace(/\/+$/, '');
  
    // אם אין סכימה – נוסיף https:// (ל-S3/GA זה נכון)
    const withScheme =
      /^https?:\/\//i.test(s) ? s :
      s.startsWith('//') ? `https:${s}` :
      `https://${s}`;
  
    try {
      return new URL(withScheme).origin;
    } catch {
      return withScheme; // fallback אחרון
    }
  };

  const siteOrigin = (() => {
    try { return new URL(SITE_BASE).origin; }
    catch { return String(SITE_BASE).replace(/\/+$/, ''); }
  })();

  const toDnsPrefetch = (origin: string) => {
    try {
      const u = new URL(origin);
      return `//${u.host}`; // בלי סכימה
    } catch {
      // fallback: אם הגיע כבר //host או host
      const s = String(origin).trim();
      if (s.startsWith('//')) return s;
      return `//${s.replace(/^https?:\/\//i, '').replace(/\/.*$/, '')}`;
    }
  };

  // 3) preload וידאו (כמו אצלך)
  const enableVideoPreload = mode === 'spa' && preloadVideo && !!videoUrl;
  const videoPreload =
    enableVideoPreload
      ? `\n  <link rel="preload" as="video" href="${escapeHtml(videoUrl!)}" type="video/mp4" crossorigin="anonymous">`
      : '';

  const {
    mobile: heroMobile,
    desktop: heroDesktop,
    mobileSrcSet: heroMobileSrcSet,
    desktopSrcSet: heroDesktopSrcSet,
  } = getInitialHeroImageAssets(eventType, loaderImage);
  const heroMobileSrcSetAttrs = heroMobileSrcSet
    ? ` imagesrcset="${escapeHtml(heroMobileSrcSet)}" imagesizes="100vw"`
    : '';
  const heroDesktopSrcSetAttrs = heroDesktopSrcSet
    ? ` imagesrcset="${escapeHtml(heroDesktopSrcSet)}" imagesizes="100vw"`
    : '';
  const heroPreload = loaderImage
    ? `
  <link rel="preload" as="image" href="${escapeHtml(loaderImage)}" type="image/webp" fetchpriority="high">`
    : `
  <link rel="preload" as="image" href="${escapeHtml(heroMobile)}" type="image/webp" fetchpriority="high" media="(max-width: 767px)"${heroMobileSrcSetAttrs}>
  <link rel="preload" as="image" href="${escapeHtml(heroDesktop)}" type="image/webp" fetchpriority="high" media="(min-width: 768px)"${heroDesktopSrcSetAttrs}>`;

  const addExternalResourceOrigin = (href?: string) => {
    const raw = String(href || '').trim();
    if (!raw || raw.startsWith('/')) return;

    try {
      const origin = new URL(raw.startsWith('//') ? `https:${raw}` : raw).origin;
      if (origin !== siteOrigin) originSet.add(origin);
    } catch {
      // ignore
    }
  };

  addExternalResourceOrigin(heroMobile);
  addExternalResourceOrigin(heroDesktop);
  addExternalResourceOrigin(POSTER_URL);
  if (enableVideoPreload) addExternalResourceOrigin(videoUrl);

  const preconnectTags = Array.from(originSet)
    .map((raw) => {
      const origin = toOrigin(raw);

      return `
  <link rel="preconnect" href="${escapeHtml(origin)}">
  <link rel="dns-prefetch" href="${escapeHtml(toDnsPrefetch(origin))}">`;
  })
    .join('');

  return `${preconnectTags}${heroPreload}${videoPreload}`.trim();
}

export const appIcons = () => `
  <link rel="icon" href="/favicon.ico" sizes="any" />
  <link rel="icon" href="/favicon-16x16.png" type="image/png" sizes="16x16" />
  <link rel="icon" href="/favicon-32x32.png" type="image/png" sizes="32x32" />
  <link rel="icon" href="/favicon-48x48.png" type="image/png" sizes="48x48" />
  <link rel="apple-touch-icon" href="/favicon-48x48.png" sizes="48x48" />
  <script>(function(){function addManifest(){if(document.querySelector('link[rel="manifest"]'))return;var link=document.createElement('link');link.rel='manifest';link.href='/manifest.json';document.head.appendChild(link)}function schedule(){if('requestIdleCallback'in window){window.requestIdleCallback(addManifest,{timeout:2000})}else{setTimeout(addManifest,1000)}}if(document.readyState==='complete'){schedule()}else{window.addEventListener('load',schedule,{once:true})}})();</script>
`.trim();

export function ssrVideoSection(poster: string, id: string, videoUrl: string) {
  if (!INCLUDE_SSR_VIDEO) return '';
  return `
  <section id="ssr-video-section" style="max-width:720px;margin:24px auto;">
    <video id="${id}" src="${escapeHtml(videoUrl)}" controls playsinline preload="metadata"
      style="width:100%;height:auto;border-radius:16px;background:#000"
      poster="${escapeHtml(poster)}" width="1280" height="720" crossorigin="anonymous">
      מצטערים, הדפדפן לא תומך בניגון וידאו. <a href="${escapeHtml(videoUrl)}">פתיחת הווידאו</a>
    </video>
    <noscript>לצפייה ללא JavaScript: <a href="${escapeHtml(videoUrl)}">פתחו את הווידאו</a></noscript>
  </section>`.trim();
}

const escAttr = (s: string) =>
  String(s).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

const initialLoaderCopy = (lang: string) => {
  const normalized = String(lang || 'he').toLowerCase();

  if (normalized.startsWith('en')) {
    return { label: 'Loading', subtitle: 'Preparing the dance floor...' };
  }

  if (normalized.startsWith('fr')) {
    return { label: 'Chargement', subtitle: 'Préparation de la piste...' };
  }

  if (normalized.startsWith('ru')) {
    return { label: 'Загрузка', subtitle: 'Готовим танцпол...' };
  }

  return { label: 'טוען', subtitle: 'מכין את הרחבה...' };
};

function buildInitialLoader(args: {
  brand: string;
  title: string;
  lang: string;
  dir: 'rtl' | 'ltr';
  eventType?: EventType;
  image?: string;
}) {
  const copy = initialLoaderCopy(args.lang);
  const {
    mobile: loaderMobileImage,
    desktop: loaderDesktopImage,
    mobileSrcSet,
    desktopSrcSet,
  } = getInitialHeroImageAssets(args.eventType, args.image);
  const mobileSrcSetAttr = mobileSrcSet
    ? ` srcset="${escAttr(mobileSrcSet)}"`
    : '';
  const desktopSrcSetAttr = desktopSrcSet || loaderDesktopImage;

  return `
  <style id="human-loader-style">#human-loader{position:fixed;inset:0;z-index:2147483000;display:block;overflow:hidden;background:#000;color:#fff;transition:opacity .2s ease,visibility .2s ease}#human-loader .hl-bg-picture{position:absolute;inset:0;z-index:0;display:block;overflow:hidden}#human-loader .hl-bg{display:block;width:100%;height:100%;object-fit:cover;object-position:center;background:#000}#human-loader:after{content:"";position:absolute;inset:0;z-index:0;background:linear-gradient(180deg,rgba(0,0,0,.74),rgba(0,0,0,.34) 45%,rgba(0,0,0,.78))}#human-loader .hl-bars{position:absolute;z-index:1;left:50%;bottom:calc(env(safe-area-inset-bottom,0px) + 34px);display:flex;align-items:end;justify-content:center;gap:5px;height:22px;transform:translateX(-50%)}#human-loader .hl-bars span{display:block;width:5px;height:10px;border-radius:999px;background:#d8a640;box-shadow:0 0 18px rgba(216,166,64,.55);animation:hl-bars .78s ease-in-out infinite}#human-loader .hl-bars span:nth-child(2){animation-delay:.1s}#human-loader .hl-bars span:nth-child(3){animation-delay:.2s}#human-loader .hl-bars span:nth-child(4){animation-delay:.3s}html.spa-ready #human-loader{opacity:0;visibility:hidden;pointer-events:none}@keyframes hl-bars{0%,100%{height:8px;opacity:.62}50%{height:22px;opacity:1}}@media (prefers-reduced-motion:reduce){#human-loader{transition:none}#human-loader .hl-bars span{animation:none;height:14px}}</style>
  <noscript><style>#human-loader{display:none!important}</style></noscript>
  <script>(function(){function add(c){try{document.documentElement.classList.add(c)}catch(e){}}function rm(c){try{document.documentElement.classList.remove(c)}catch(e){}}function has(c){try{return document.documentElement.classList.contains(c)}catch(e){return false}}function removeById(id){try{var el=document.getElementById(id);if(el&&el.parentNode)el.parentNode.removeChild(el)}catch(e){}}function hideLoader(){add('spa-ready');setTimeout(function(){removeById('human-loader')},280)}var done=false;add('has-js');window.__spaReady=function(){if(done)return;done=true;hideLoader();removeById('prerender-shell')};setTimeout(function(){if(done||has('spa-ready'))return;rm('has-js');rm('spa-ready');removeById('human-loader')},8000)})();</script>
  <div id="human-loader" dir="${escAttr(args.dir)}" aria-label="${escAttr(copy.label)}" aria-live="polite" role="status">
    <picture class="hl-bg-picture" aria-hidden="true">
      <source media="(min-width:768px)" srcset="${escAttr(desktopSrcSetAttr)}" sizes="100vw">
      <img class="hl-bg" src="${escAttr(loaderMobileImage)}"${mobileSrcSetAttr} sizes="100vw" alt="" fetchpriority="high" loading="eager" decoding="sync" width="1920" height="1080" draggable="false">
    </picture>
    <div class="hl-bars" aria-hidden="true"><span></span><span></span><span></span><span></span></div>
  </div>`.trim();
}

export function buildHtmlPage(args: {
  lang: string;
  dir?: 'rtl' | 'ltr';
  title: string;
  desc: string;
  urlWithSlash: string;
  ogImage: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogLocale?: string;
  brand: string;
  jsonLd?: object[];
  bodyAboveRoot?: string;
  preloadOgImage?: boolean;
  loaderImage?: string;
  preloadVideo?: boolean;
  mode?: 'prerender' | 'spa';
  ogVideoUrl?: string;
  ogVideoWidth?: number | string;
  ogVideoHeight?: number | string;
  metaPixelId?: string;
  spaHeadLinks?: string;
  spaRoutePreloadLinks?: string;
  spaBodyScripts?: string;
  gaMeasurementId?: string;
  eventType?: EventType;
}) {
  const canonicalAbs = (() => {
    const raw = String(args.urlWithSlash || '').trim();
    try {
      const u = new URL(raw);
      if (!u.pathname.endsWith('/')) u.pathname += '/';
      return u.toString();
    } catch {
      return buildCanonicalUrl(raw);
    }
  })(); 
  const clean = (s: string) => String(s).replace(/\s+/g, ' ').trim();
  const title = clean(args.title);
  const desc = clean(args.desc);
  const shouldIncludeSpa = true;
  const spaHead = shouldIncludeSpa ? (args.spaHeadLinks || '') : '';
  const spaRoutePreloads = shouldIncludeSpa ? (args.spaRoutePreloadLinks || '') : '';
  const spaBody = shouldIncludeSpa ? (args.spaBodyScripts || '') : '';

const perfHints = performanceHints({
  ogImage: args.ogImage,
  preloadOgImage: args.preloadOgImage,
  videoUrl: args.ogVideoUrl,
  preloadVideo: args.preloadVideo,
  mode: args.mode,
  eventType: args.eventType,
  loaderImage: args.loaderImage,
});

  const canonicalTag = `<link rel="canonical" href="${escAttr(canonicalAbs)}">`;

  const ogType = args.ogVideoUrl ? 'video.other' : 'website';

  const ogVideoTags = args.ogVideoUrl
    ? `
  <meta property="og:video" content="${escAttr(args.ogVideoUrl)}">
  <meta property="og:video:secure_url" content="${escAttr(args.ogVideoUrl)}">
  <meta property="og:video:type" content="video/mp4">
  <meta property="og:video:width" content="${escAttr(String(args.ogVideoWidth ?? 1280))}">
  <meta property="og:video:height" content="${escAttr(String(args.ogVideoHeight ?? 720))}">`
    : '';

  const jsonLdTag = buildJsonLdScriptTag(args.jsonLd as any[], canonicalAbs);


  const robots = escAttr(IS_PROD ? 'index,follow' : 'noindex,follow');
  const robotsFull = `${robots},max-image-preview:large`;

/*
  const gaHead =
  IS_PROD && gaMeasurementId
    ? `
<script>
  (function(){
    var id='${escAttr(gaMeasurementId)}';

    function initGA(){
      if (window.__gaLoaded) return;
      window.__gaLoaded = true;

      var s=document.createElement('script');
      s.async=true;
      s.src='https://www.googletagmanager.com/gtag/js?id='+id;
      document.head.appendChild(s);

      window.dataLayer=window.dataLayer||[];
      function gtag(){dataLayer.push(arguments);}
      window.gtag=gtag;
      gtag('js', new Date());
      gtag('config', id, { send_page_view: false });
    }

    if ('requestIdleCallback' in window) requestIdleCallback(initGA,{timeout:2000});
    else setTimeout(initGA, 1500);
  })();
</script>`.trim()
    : '';



  // ✅ Meta Pixel SCRIPT בלבד ב-head (אין noscript ב-head!)
  const metaPixelHeadScript = args.metaPixelId
    ? `
  <!-- Meta Pixel Code -->
  <script>
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
    (window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${escAttr(args.metaPixelId)}');
    fbq('track', 'PageView');
  </script>
  <!-- End Meta Pixel Code -->`
    : '';

  // ✅ Meta Pixel NOSCRIPT חייב להיות ב-body
  const metaPixelBodyNoScript = args.metaPixelId
    ? `
  <!-- Meta Pixel (noscript) -->
  <noscript>
    <img
      height="1"
      width="1"
      style="display:none"
      alt=""
      src="https://www.facebook.com/tr?id=${escAttr(args.metaPixelId)}&ev=PageView&noscript=1">
  </noscript>`
    : '';
*/

const hreflangTags = [
  ...(() => {
    const langCode = clean(args.lang || 'he') || 'he';
    const fullLangCode = clean(args.ogLocale || '')
      .replace(/_/g, '-')
      .replace(/^he-IL$/i, 'he-IL');

    if (langCode === 'he') {
      return [
        `<link rel="alternate" hreflang="he" href="${escAttr(canonicalAbs)}">`,
        `<link rel="alternate" hreflang="he-IL" href="${escAttr(canonicalAbs)}">`,
        `<link rel="alternate" hreflang="x-default" href="${escAttr(canonicalAbs)}">`,
      ];
    }

    const values = Array.from(new Set([langCode, fullLangCode].filter(Boolean)));
    return values.map(
      (value) => `<link rel="alternate" hreflang="${escAttr(value)}" href="${escAttr(canonicalAbs)}">`
    );
  })(),
].join('\n  ');


const earlyJsClassScript = shouldIncludeSpa
  ? '<script>document.documentElement.classList.add("has-js")</script>'
  : '';
const globalReset = '<style>body,html{margin:0;padding:0;overflow-x:hidden;background:#000}html{scrollbar-gutter:stable}#root{min-height:100vh;background:#000}html.has-js #prerender-content{position:absolute!important;width:1px!important;height:1px!important;margin:0!important;padding:0!important;overflow:hidden!important;clip:rect(0 0 0 0)!important;clip-path:inset(50%)!important;white-space:nowrap!important;border:0!important}</style>';
const ogImageWidth = Number(args.ogImageWidth || 1200);
const ogImageHeight = Number(args.ogImageHeight || 630);
const ogImageType = (() => {
  const cleanUrl = String(args.ogImage || '').split('?')[0].toLowerCase();
  if (cleanUrl.endsWith('.webp')) return 'image/webp';
  if (cleanUrl.endsWith('.png')) return 'image/png';
  return 'image/jpeg';
})();
const htmlDir = args.dir ?? (String(args.lang || 'he').toLowerCase().startsWith('he') ? 'rtl' : 'ltr');
const ogLocale = clean(args.ogLocale || (args.lang === 'he' ? 'he-IL' : args.lang || 'he'))
  .replace(/-/g, '_');
const initialLoader = shouldIncludeSpa
  ? buildInitialLoader({
      brand: args.brand,
      title,
      lang: args.lang,
      dir: htmlDir,
      eventType: args.eventType,
      image: args.loaderImage,
    })
  : '';
const criticalFontHints = `
  <link rel="preload" as="font" href="/fonts/Rubik-ExtraBold.woff2" type="font/woff2" crossorigin>
  <link rel="preload" as="font" href="/fonts/Rubik-BoldItalic.woff2" type="font/woff2" crossorigin>
  <style>@font-face{font-family:Rubik-ExtraBold;src:url('/fonts/Rubik-ExtraBold.woff2') format('woff2');font-weight:800;font-style:normal;font-display:optional}@font-face{font-family:Rubik-BoldItalic;src:url('/fonts/Rubik-BoldItalic.woff2') format('woff2');font-weight:700;font-style:italic;font-display:optional}</style>
`.trim();

return `<!DOCTYPE html>
<html lang="${escAttr(args.lang)}" dir="${escAttr(htmlDir)}">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">

  ${perfHints}
  ${globalReset}
  ${earlyJsClassScript}
  ${appIcons()}

 <meta name="robots" content="${robotsFull}">

  ${canonicalTag}
  ${hreflangTags}

  <title>${escAttr(title)}</title>
  <meta name="description" content="${escAttr(desc)}">

  <!-- Open Graph -->
  <meta property="og:type" content="${ogType}">
  <meta property="og:site_name" content="${escAttr(args.brand)}">
  <meta property="og:title" content="${escAttr(title)}">
  <meta property="og:description" content="${escAttr(desc)}">
  <meta property="og:url" content="${escAttr(canonicalAbs)}">

  <meta property="og:image" content="${escAttr(args.ogImage)}">
  <meta property="og:image:secure_url" content="${escAttr(args.ogImage)}">
  <meta property="og:image:type" content="${escAttr(ogImageType)}">
  <meta property="og:image:alt" content="${escAttr(args.brand)}">
  <meta property="og:image:width" content="${ogImageWidth}">
  <meta property="og:image:height" content="${ogImageHeight}">
  <meta property="og:locale" content="${escAttr(ogLocale)}">
  ${ogVideoTags}

  <!-- Twitter -->
  <meta name="twitter:card" content="summary_large_image">
  <meta name="twitter:title" content="${escAttr(title)}">
  <meta name="twitter:description" content="${escAttr(desc)}">
  <meta name="twitter:image" content="${escAttr(args.ogImage)}">
  <meta name="twitter:image:alt" content="${escAttr(args.brand)}">

  <!-- SPA -->
  ${spaHead}
  ${spaRoutePreloads}

  ${criticalFontHints}
</head>

<body>
  ${initialLoader}
  <div id="root"></div>
  ${spaBody}
  ${args.bodyAboveRoot || ''}

  <!-- JSON-LD -->
  ${jsonLdTag}

  <noscript>${escAttr(args.brand)}</noscript>
</body>
</html>`;
}

export type BuildServicePageGraphOpts = {
  canonicalWithSlash: string;
  titleNoBrand: string;
  fullTitle?: string;
  description: string;
  ogImageAbs?: string;
  inLanguage?: string;

  serviceLabel: string;
  cityOrVenue: string;
  alternateNames?: string[];

  datePublished?: string;
  dateModified?: string;

  includeVideo?: boolean;
  video?: {
    siteBase: string;
    title: string;
    videoUrl: string;
    uploadDate: string;
    durationISO8601: string;
  };

  faqItems?: Array<{ q: string; a: string }>;
  section4Links?: Array<{ href: string; label: string }>;

  includeSiteNodes?: boolean;

  businessAreaName?: string;
  businessOffice?: BusinessOffice;

    // ✅ חדש: חיבור לפי סוג אירוע
    eventType?: EventType | string;

    reviews?: Array<{
      rating: number;
      author?: string;
      text?: string;
      dateISO?: string;
      source?: 'google' | string;
      profileUrl?: string;
    }>;

};

export function buildServicePageGraph(opts: BuildServicePageGraphOpts) {
  const {
    canonicalWithSlash,
    titleNoBrand,
    fullTitle,
    description,
    ogImageAbs = OG_PRIMARY_IMAGE,
    inLanguage = HTML_LANG_FULL,
    serviceLabel,
    cityOrVenue,
    alternateNames = [],
    datePublished,
    dateModified,
    includeVideo = false,
    video,
    faqItems = [],
    section4Links = [],
    includeSiteNodes = true,
    businessAreaName,
    businessOffice,
    eventType,
    reviews,
  } = opts;

  const stripCtx = (o: any) => { if (!o) return o; const { '@context': _c, ...rest } = o; return rest; };
  const withId = (id: string, node: any) => ({ ...stripCtx(node), '@id': id });
  
  const enhanced = buildSeoEventBundle({
    pageUrlAbsWithSlash: canonicalWithSlash,
    pageTitle: titleNoBrand,
    pageDesc: description,
    serviceLabel,
    locationLabel: businessAreaName || cityOrVenue || 'ישראל',
    eventType,
    navLinks: mapInternalLinksEnhanced(section4Links),
    reviews,
    businessOffice,
    video: includeVideo && video ? {
      contentUrl: video.videoUrl,
      uploadDateISO: video.uploadDate,
      durationISO8601: video.durationISO8601,
    } : undefined,
  });

  const loc = String(cityOrVenue ?? '').trim();
  const titleCore = loc ? `${serviceLabel} ב${loc}` : serviceLabel;
  const finalTitleNoBrand = String(fullTitle || `${titleCore} - ${BRAND_TITLE_HE}`)
    .replace(/\s+/g, ' ')
    .trim();
  const finalDescription = description || enhanced.description;
  const service = buildServiceLd(
    serviceLabel,
    cityOrVenue,
    false,
    {
      inLanguage,
      alternateNames: alternateNames.slice(0, 10).filter(Boolean),
      mainEntityOfPageId: `${canonicalWithSlash}#webpage`,
    }
  );

  // ✅ כאן היה הבאג אצלך — השתמש ב-finalTitleNoBrand/finalDescription
  const webPage = buildWebPageLd({
    name: stripBrandAnywhere(finalTitleNoBrand), // WebPage.name בלי מותג כפול
    url: canonicalWithSlash,
    description: finalDescription,
    primaryImage: ogImageAbs,
    inLanguage,
    datePublished,
    dateModified,
    breadcrumbId: `${canonicalWithSlash}#breadcrumb`,
    mainEntityId: `${canonicalWithSlash}#service`,
  });

  const faqLd      = faqItems.length ? buildFaqLd(faqItems) : null;
  const itemListLd = section4Links.length ? buildItemListLd(section4Links) : null;

  const siteNodes = includeSiteNodes
    ? [
        withId(
          `${SITE_BASE}#business`,
          buildBusinessLd({
            includeAreaServed: false,
            includeOpeningHours: true,
            office: businessOffice,
          })
        ),
        withId(`${SITE_BASE}#website`, buildWebsiteLd()),
        withId(`${SITE_BASE}#logo`, buildLogoLd()),
      ]
    : [];

  const webPagePatched = {
    ...webPage,
    isPartOf: { '@id': `${SITE_BASE}#website` },
    url: canonicalWithSlash,
    mainEntity: { '@id': `${canonicalWithSlash}#service` },
    hasPart: [{ '@id': `${canonicalWithSlash}#s6-reviews` }],
  };

  const enhancedNodesNoCtx = (enhanced.enhancedNodes || []).map(stripCtx);

  const graph = [
    ...siteNodes,
    withId(`${canonicalWithSlash}#service`, service),
    withId(`${canonicalWithSlash}#webpage`, webPagePatched),
    faqLd      ? withId(`${canonicalWithSlash}#faq`, faqLd) : undefined,
    itemListLd ? withId(`${canonicalWithSlash}#s4-items`, itemListLd) : undefined,
    ...enhancedNodesNoCtx,
  ].filter(Boolean) as object[];

  return {
    // ✅ חשוב: להחזיר גם טקסטים לשימוש ב-page-specs
    titleNoBrand: finalTitleNoBrand,
    description: finalDescription,
    ldGraph: { '@context': 'https://schema.org', '@graph': graph },
  };
}
